import { storeAccessToken } from '@mulliganfunding/api-micro-auth'
import type { AuthnTransaction, OktaAuth } from '@okta/okta-auth-js'
import { useOktaAuth } from '@okta/okta-react'
import { useLocation } from 'wouter'
import { useState } from 'react'

import { BrandedContentPanel } from '../../../components/branded-content-panel/branded-content-panel.tsx'
import { OktaSignIn } from '../../../components/okta-sign-in.tsx'
import { TwoColumnPage } from '../../../components/two-column-page/two-column-page.tsx'
import { StatusBanner } from '../../../components/status-banner/status-banner.tsx'
import { BannerType } from '../../../components/status-banner/banner-type.ts'
import {
  PATH_DASHBOARD,
  PATH_LOGIN_MAGIC_LINK,
  PATH_START_APPLICATION,
} from '../../../constants/paths.ts'

import envelopeIcon from '../../../assets/imgs/link-email-icon.svg'
import './login.css'

export const Login = () => {
  const { authState } = useOktaAuth()
  const setLocation = useLocation()[1]
  const [displayError, setDisplayError] = useState<boolean>(false)

  const onSuccess = (
    oktaAuth: OktaAuth,
    transaction: AuthnTransaction,
    mfAccessToken: string,
  ) => {
    storeAccessToken(mfAccessToken)
    /* TODO: If you need to see what is going on before redirect - uncomment this section and
       comment out the oktaAuth?.signInWithRedirect line
    */
    // console.log({ transaction, oktaAuth, mfAccessToken })
    // setTimeout(() => {
    //   oktaAuth?.signInWithRedirect({ sessionToken: transaction.sessionToken })
    // }, 20000)
    oktaAuth?.signInWithRedirect({ sessionToken: transaction.sessionToken })
  }

  const onError = (err: { message: string; error: any }) => {
    console.log('Sign in error:', err.message, err.error)
    setDisplayError(true)
  }

  if (!authState) {
    return <div>Loading...</div>
  }

  if (authState.isAuthenticated) {
    window.location.assign(`${window.location.origin}/${PATH_DASHBOARD}`)
    return null
  }

  return (
    <TwoColumnPage>
      {/* Skip Link for screen readers and keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <BrandedContentPanel title="Welcome Back">
        <h1 id="main-content" className="visually-hidden">
          Sign in to your Mulligan Funding account
        </h1>
        <div className="explanatory-text typog-body-large">
          Sign in to access your Mulligan Funding account.
        </div>
        {displayError && (
          <StatusBanner
            key="login-error"
            type={BannerType.ERROR}
            message="User could not be authenticated."
            onDismissClick={() => setDisplayError(false)}
          />
        )}
        <OktaSignIn onSuccess={onSuccess} onError={onError} />

        <button
          className="magic-link-button"
          onClick={() => {
            const magicLinkPath = `${window.location.origin}${PATH_LOGIN_MAGIC_LINK}`
            setLocation(magicLinkPath)
          }}
          aria-label="Sign in using your email only"
        >
          <img
            className="envelope-icon"
            src={envelopeIcon}
            alt=""
            role="presentation"
          />
          <span>Email Only Sign In</span>
        </button>

        <div className="alternate-nav">
          <span>Not a customer?</span>
          <a
            className="link no-underline"
            href={`${window.location.origin}/${PATH_START_APPLICATION}`}
            aria-label="Apply for a loan"
          >
            Apply For a Loan
          </a>
        </div>
      </BrandedContentPanel>
    </TwoColumnPage>
  )
}
